import { ALL_CLIENT_TYPES, type ClientType } from '../constants/client-types';
import { EXPERIENCE_RANGE_KEYS, type ExperienceRangeKey } from '../constants/experience-ranges';
import { FEE_OPTION_KEYS, type FeeOptionKey } from '../constants/fee-options';
import { ALL_SERVICES, type Service } from '../constants/services';
import { SORT_KEYS, type Sort } from '../constants/sort';
import type { SearchParams } from '../types';
import { stripNullBytes } from '../utils/strip-null-bytes';
import { classifyQuery } from './classify-query';

export type ParseFindAdvisorSearchParamsResult =
  { kind: 'ok'; params: SearchParams } | { kind: 'redirect' };

/** The untyped shape a router hands over: repeated keys arrive as arrays. */
export type RawSearchParams = { [k: string]: string | string[] | undefined };

function first(value: string | string[] | undefined): string | undefined {
  if (value === undefined) return undefined;
  return Array.isArray(value) ? value[0] : value;
}

function asArray(value: string | string[] | undefined): string[] {
  if (value === undefined) return [];
  return Array.isArray(value) ? value : [value];
}

function coercePage(raw: string | string[] | undefined): number {
  if (raw === undefined) return 1;
  const value = first(raw);
  const n = Number(value);
  if (!Number.isFinite(n)) return 1;
  const floored = Math.floor(n);
  return floored >= 1 ? floored : 1;
}

function pickValid<T extends string>(
  raw: string | string[] | undefined,
  allowed: ReadonlyArray<T>,
): T[] {
  const set = new Set<string>(allowed);
  return asArray(raw).filter((v): v is T => set.has(v));
}

/**
 * Parses raw URL search params into `SearchParams` for the find-an-advisor
 * results route, or signals that the caller should fall back to the landing
 * page. Unrecognised filter and sort values are dropped silently.
 *
 * Distinct from `parseSearchParams` in `@ria-client/advisor-search/service`,
 * which validates an already-typed object at the server data boundary.
 */
export function parseFindAdvisorSearchParams(
  input: RawSearchParams,
): ParseFindAdvisorSearchParamsResult {
  const qRaw = first(input.q);
  const q = typeof qRaw === 'string' ? stripNullBytes(qRaw).trim() : '';

  if (classifyQuery(q) === 'invalid') return { kind: 'redirect' };

  const page = coercePage(input.page);
  const services = pickValid<Service>(input.services, ALL_SERVICES);
  const fees = pickValid<FeeOptionKey>(input.fees, FEE_OPTION_KEYS);
  const clientTypes = pickValid<ClientType>(input.clientTypes, ALL_CLIENT_TYPES);
  const experience = pickValid<ExperienceRangeKey>(input.experience, EXPERIENCE_RANGE_KEYS);
  const [sort] = pickValid<Sort>(input.sort, SORT_KEYS);

  const params: SearchParams = { q, page };
  if (services.length) params.services = services;
  if (fees.length) params.fees = fees;
  if (clientTypes.length) params.clientTypes = clientTypes;
  if (experience.length) params.experience = experience;
  if (sort !== undefined) params.sort = sort;

  return { kind: 'ok', params };
}
